import { Link } from "react-router-dom";
import {
  Github,
  Linkedin,
  Twitter,
  Mail,
  ArrowRight,
  Youtube,
} from "lucide-react";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gray-950">
      {/* Promotional Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-500 to-blue-500 pt-8 pb-4 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse"></span>
            <p className="text-white font-semibold text-sm lg:text-base tracking-wide">
              Expensehut POS now available in 7 countries
            </p>
            <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse"></span>
          </div>
          <p className="text-blue-50 text-xs lg:text-sm">
            No hardware. No Contract. Get{" "}
            <a
              className="text-blue-100 underline"
              target="_blank"
              href="https://www.expensehut.com"
            >
              Quote
            </a>
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-24 pb-24 px-6 lg:px-8 bg-gradient-to-b from-gray-950 to-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12 items-start">
            {/* Photo - Left Side */}
            <div className="md:col-span-2 flex justify-center md:justify-start">
              <div className="w-56 h-56 rounded-full bg-gray-800 border border-gray-700 overflow-hidden shadow-lg flex-shrink-0">
                <img
                  src="sabarishnarain.jpg"
                  alt="Sabarish Narain"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
            </div>

            {/* Bio - Right Side */}
            <div className="md:col-span-3 space-y-6">
              <div className="space-y-3">
                <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Building the future of smart finance.
                </h1>
                <p className="text-xl text-gray-400">Co-Founder - Expensehut</p>
              </div>

              <div className="space-y-4 text-gray-300 text-lg leading-relaxed max-w-2xl">
                <p>
                  I’m a father, husband, and technologist who strives to solve
                  real-world problems in personal and business finance. With a
                  focus on simplicity and performance, we're creating tools that
                  actually get used.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <a
                  href="mailto:sabarish.narain@expensehut.com"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  <Mail size={18} />
                  Get in touch
                </a>
                <a
                  href="https://linkedin.com/in/sabarishnarain"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="text-gray-400 hover:text-white transition mx-4 mt-2"
                >
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section
        id="projects"
        className="py-24 px-6 lg:px-8 border-t border-gray-800 bg-gray-900"
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-16"></div>

          <div className="space-y-12">
            <a
              href="https://business.expensehut.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <div className="grid md:grid-cols-2 gap-8 items-center">
                {/* Image */}
                <div className="rounded-xl overflow-hidden aspect-video shadow-lg group-hover:shadow-xl transition-shadow">
                  <img
                    src="/business.png"
                    alt="Expensehut"
                    className="w-90 h-full object-cover group-hover:scale-70 transition-transform duration-300"
                  />
                </div>

                {/* Content */}
                <div className="space-y-5">
                  <div>
                    <h3 className="text-3xl font-bold text-white mb-2">
                      Expensehut POS
                    </h3>
                    <p className="text-gray-400 text-lg mb-4">
                      A lightning-fast, no-contract point-of-sale system built
                      for modern retail and hospitality businesses. Process
                      payments instantly and keep your business running
                      smoothly.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                      Features
                    </p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                        Instant payment processing and settlements
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                        Inventory management and stock tracking
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                        Real-time sales analytics and reporting
                      </li>
                    </ul>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <div className="inline-flex items-center gap-2 text-blue-400 font-semibold group-hover:gap-3 transition-all">
                      Visit
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </div>
              </div>
            </a>
            {/* Expensehut Project */}
            <a
              href="https://app.expensehut.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <div className="grid md:grid-cols-2 gap-8 items-center">
                {/* Image */}

                {/* Content */}
                <div className="space-y-5">
                  <div>
                    <h3 className="text-3xl font-bold text-white mb-2">
                      Money Tracker
                    </h3>
                    <p className="text-gray-400 text-lg mb-4">
                      A smart expense management platform designed to help
                      individuals collectively see all their transactions from
                      all credit cards in one place. Built for individuals who
                      wanted to have full control of their spending.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                      Features
                    </p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                        Real-time expense tracking and categorization
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                        Advanced analytics and spending insights
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                        Multi-currency and team collaboration
                      </li>
                    </ul>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <div className="inline-flex items-center gap-2 text-blue-400 font-semibold group-hover:gap-3 transition-all">
                      Visit Expensehut
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </div>

                <div className="rounded-xl overflow-hidden bg-gray-800 aspect-video shadow-lg group-hover:shadow-xl transition-shadow">
                  <img
                    src="/iphone-exhut.png"
                    alt="Expensehut"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        className="py-24 px-6 lg:px-8 bg-gray-900 border-t border-gray-800"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-12">About</h2>

          <div className="space-y-8 text-gray-300 text-lg leading-relaxed">
            <p>
              I'm a software founder with a passion for building financial tools
              that solve real problems. With deep expertise in fintech, product
              strategy, and engineering, I've spent the last decade guiding
              products that are intuitive, fast, and genuinely useful.
            </p>

            <p>
              At Expensehut, I lead the vision to simplify financial management
              for businesses of all sizes. Our products have helped thousands of
              businesses gain better control over their finances and make
              smarter financial decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
    </div>
  );
}
